import {
  createRootRoute,
  createRoute,
  createRouter,
  lazyRouteComponent,
  Navigate,
  Outlet,
  type RouteComponent,
} from "@tanstack/react-router";
import { AppShell } from "./components/layout/app-shell";
import { useTempoReal } from "./lib/tempo-real";
import {
  LayoutAtendimentos,
  SemConversaSelecionada,
  TelaConversa,
  validarBuscaAtendimentos,
} from "./routes/atendimentos";
import { validarBuscaConfiguracoes } from "./routes/configuracoes-busca";

// Telas fora do atendimento carregam sob demanda: o celular abre a conversa
// sem baixar configurações, equipe e clientes.
const TelaConfiguracoes = lazyRouteComponent(
  () => import("./routes/configuracoes"),
  "TelaConfiguracoes",
);
const TelaFunil = lazyRouteComponent(() => import("./routes/funil"), "TelaFunil");
const TelaClientes = lazyRouteComponent(() => import("./routes/clientes"), "TelaClientes");
const TelaEquipe = lazyRouteComponent(() => import("./routes/equipe"), "TelaEquipe");
const TelaPainel = lazyRouteComponent(() => import("./routes/painel"), "TelaPainel");
const TelaFluxos = lazyRouteComponent(() => import("./routes/fluxos"), "TelaFluxos");
const TelaEditorFluxo = lazyRouteComponent(
  () => import("./routes/fluxo-editor"),
  "TelaEditorFluxo",
);
const TelaInteligencia = lazyRouteComponent(
  () => import("./routes/inteligencia"),
  "TelaInteligencia",
);

function Raiz() {
  const conexao = useTempoReal();
  return (
    <AppShell conexao={conexao}>
      <Outlet />
    </AppShell>
  );
}

const raiz = createRootRoute({ component: Raiz });

const inicio = createRoute({
  getParentRoute: () => raiz,
  path: "/",
  component: () => <Navigate to="/atendimentos" />,
});

const atendimentos = createRoute({
  getParentRoute: () => raiz,
  path: "/atendimentos",
  validateSearch: validarBuscaAtendimentos,
  component: LayoutAtendimentos,
});
const atendimentosIndice = createRoute({
  getParentRoute: () => atendimentos,
  path: "/",
  component: SemConversaSelecionada,
});
const conversa = createRoute({
  getParentRoute: () => atendimentos,
  path: "$conversaId",
  component: TelaConversa,
});

const simples = (path: string, component: RouteComponent) =>
  createRoute({ getParentRoute: () => raiz, path, component });

const arvore = raiz.addChildren([
  inicio,
  atendimentos.addChildren([atendimentosIndice, conversa]),
  simples("/funil", TelaFunil),
  simples("/clientes", TelaClientes),
  simples("/equipe", TelaEquipe),
  simples("/painel", TelaPainel),
  simples("/inteligencia", TelaInteligencia),
  simples("/fluxos", TelaFluxos),
  simples("/fluxos/$fluxoId", TelaEditorFluxo),
  createRoute({
    getParentRoute: () => raiz,
    path: "/configuracoes",
    validateSearch: validarBuscaConfiguracoes,
    component: TelaConfiguracoes,
  }),
  simples("*", () => <Navigate to="/atendimentos" />),
]);

export const router = createRouter({ routeTree: arvore, defaultPreload: "intent" });

declare module "@tanstack/react-router" {
  interface Register {
    router: typeof router;
  }
}
